import * as React from "react";

const KEYBOARD_VIEWPORT_DELTA = 120;

const NON_TEXT_INPUT_TYPES = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "hidden",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
]);

const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;

  const tagName = target.tagName.toLowerCase();
  if (tagName === "textarea") {
    const textarea = target as HTMLTextAreaElement;
    return !textarea.readOnly && !textarea.disabled;
  }

  if (tagName === "input") {
    const input = target as HTMLInputElement;
    const inputType = (input.type || "text").toLowerCase();
    return !NON_TEXT_INPUT_TYPES.has(inputType) && !input.readOnly && !input.disabled;
  }

  return false;
};

// Only meaningful on touch devices — desktop browsers don't have a software keyboard.
const isTouchDevice = () =>
  typeof window !== "undefined" && (navigator.maxTouchPoints > 0 || "ontouchstart" in window);

export function useMobileKeyboardOpen() {
  const [isKeyboardOpen, setIsKeyboardOpen] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === "undefined" || !isTouchDevice()) return;

    const visualViewport = window.visualViewport;
    let baselineHeight = visualViewport?.height ?? window.innerHeight;

    const updateKeyboardState = () => {
      const currentHeight = visualViewport?.height ?? window.innerHeight;

      // Only grow the baseline when no editable element is focused
      if (!isEditableTarget(document.activeElement) && currentHeight > baselineHeight) {
        baselineHeight = currentHeight;
      }

      const viewportCollapsed = baselineHeight - currentHeight > KEYBOARD_VIEWPORT_DELTA;
      setIsKeyboardOpen(viewportCollapsed);
    };

    const handleFocusOut = () => {
      window.requestAnimationFrame(updateKeyboardState);
    };

    window.addEventListener("resize", updateKeyboardState);
    visualViewport?.addEventListener("resize", updateKeyboardState);
    visualViewport?.addEventListener("scroll", updateKeyboardState);
    document.addEventListener("focusout", handleFocusOut);

    updateKeyboardState();

    return () => {
      window.removeEventListener("resize", updateKeyboardState);
      visualViewport?.removeEventListener("resize", updateKeyboardState);
      visualViewport?.removeEventListener("scroll", updateKeyboardState);
      document.removeEventListener("focusout", handleFocusOut);
    };
  }, []);

  return isKeyboardOpen;
}

